import { TTicketDetails } from '@daymade/api-contracts';
import Chevron from 'components/Chevron';
import EntryStack from 'components/enter/EntryStack';
import ImageWithFallback from 'components/ImageWithFallback';
import { CONFIRMATION_ASSET_FOLDER } from 'constants/Confirmation';
import { DaymadeDrawTypePlanCode } from 'constants/Draw';
import { TSubscriptions } from 'stores/UserStore';
import { IEntryLine } from 'types/Draws';
import { formattedPriceStringFromPence } from 'utils/common';
import styles from './YourSubscription.module.css';

interface YourSubscriptionProps {
  expanded: boolean;
  onClick: () => void;
  activeSubscription: TSubscriptions[0];
  activeTickets: TTicketDetails[];
}

export const YourSubscription = ({
  expanded,
  onClick,
  activeSubscription,
  activeTickets,
}: YourSubscriptionProps) => {
  const planPrice = formattedPriceStringFromPence(
    activeSubscription.drawOrder.price,
  );

  const paidToday = formattedPriceStringFromPence(
    activeSubscription.order.totalFiatAmount,
  );

  const days = activeSubscription.drawSchedulePlans
    .map(
      (plan) =>
        plan.drawSchedule.code.charAt(0).toUpperCase() +
        plan.drawSchedule.code.slice(1).toLowerCase() +
        's',
    )
    .join(' & ');

  const containerClass = expanded
    ? `${styles.container} ${styles.containerExpanded}`
    : styles.container;

  return (
    <div className={styles.wrapper}>
      <div
        onClick={onClick}
        className={containerClass}
        style={{ position: 'relative' }}
      >
        <div className={styles.headerContainer}>
          <div className={styles.shoppingCartImageContainer}>
            <div className={styles.shoppingCartImageWrapper}>
              <ImageWithFallback
                layout="fill"
                src={`${CONFIRMATION_ASSET_FOLDER}/shopping-cart.svg`}
              />
            </div>
          </div>
          <h3 className="noMargin aboutUsTypographyHeaderMedium">
            Your Subscription
          </h3>

          <div style={{ marginLeft: 'auto' }}>
            <Chevron reversed={expanded} direction="down" />
          </div>
        </div>

        {expanded && (
          <div className={styles.bodyContainer}>
            <div className={styles.planSection}>
              <p className={`textAlignLeft noMargin`}>
                <strong>
                  {activeSubscription.drawSchedulePlans[0].drawTypePlan.code
                    .charAt(0)
                    .toUpperCase() +
                    activeSubscription.drawSchedulePlans[0].drawTypePlan.code
                      .slice(1)
                      .toLowerCase()}{' '}
                  Plan
                </strong>
              </p>
              <p className="noMargin aboutUsTypographyBodySmall">
                {' '}
                {planPrice} / week
              </p>
            </div>

            <div className={styles.separator} />

            <div className={styles.subscriptionSection}>
              <div className={styles.headerContainer}>
                <div className={styles.subscriptionSectionImageContainer}>
                  <ImageWithFallback
                    layout="fill"
                    src={`${CONFIRMATION_ASSET_FOLDER}/calendar.svg`}
                  />
                </div>
                <p className={`textAlignLeft noMargin`}>
                  <strong>Draw Days</strong>
                </p>
                <p
                  style={{ flex: 1 }}
                  className="textAlignRight aboutUsTypographyBodySmall noMargin"
                >
                  {days}
                </p>
              </div>
            </div>

            <div className={styles.separator} />

            <div className={styles.subscriptionSection}>
              <div className={styles.headerContainer}>
                <div className={styles.subscriptionSectionImageContainer}>
                  <ImageWithFallback
                    layout="fill"
                    src={`${CONFIRMATION_ASSET_FOLDER}/entries.svg`}
                  />
                </div>
                <p className={`textAlignLeft noMargin`}>
                  <strong>Your Entries</strong>
                </p>
              </div>

              <div className={styles.entryStackList}>
                {activeTickets &&
                  activeTickets.length > 0 &&
                  activeTickets[0].entries.map((entry, index) => (
                    <div
                      key={index}
                      className={styles.entryStackItem}
                      style={{
                        zIndex: activeTickets[0].entries.length - index,
                      }}
                    >
                      <EntryStack
                        code={
                          activeSubscription.drawSchedulePlans[0].drawTypePlan
                            .code as unknown as DaymadeDrawTypePlanCode
                        }
                        entry={entry as unknown as IEntryLine}
                        stackCount={1}
                      />
                    </div>
                  ))}
              </div>
            </div>

            <div className={styles.separator} />

            <div className={styles.subscriptionSection}>
              <div className={styles.headerContainer}>
                <div className={styles.subscriptionSectionImageContainer}>
                  <ImageWithFallback
                    layout="fill"
                    src={`${CONFIRMATION_ASSET_FOLDER}/trees.svg`}
                  />
                </div>
                <p className={`textAlignLeft noMargin`}>
                  <strong>Trees Planted</strong>
                </p>
                <p
                  style={{ flex: 1 }}
                  className="textAlignRight aboutUsTypographyBodySmall noMargin"
                >
                  {activeSubscription.drawSchedulePlans.length} per week
                </p>
              </div>
            </div>

            <div className={styles.paidToday}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <p className={`textAlignLeft noMargin`}>
                  <strong>Paid Today</strong>
                </p>
                <p className="textAlignRight noMargin">{paidToday}</p>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <p className={`textAlignLeft noMargin`}>
                  <strong>Next Payment Date</strong>
                </p>
                <p className="textAlignRight noMargin">
                  {new Date(activeSubscription.subscription.nextPaymentDate)
                    .toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                    .replace(/\//g, '.')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
